import React, { useState, useEffect, useMemo } from 'react';
import { FaPlus, FaTrashAlt } from 'react-icons/fa';
import { Venue } from '../../api/apiTypes';
import { listVenues } from '../../api/functionApi';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    lista: Venue[];
    onAddToModalList: (venue: Venue) => Promise<boolean>;
};

const PlusIcon = FaPlus as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FaTrashAlt as React.ComponentType<React.SVGProps<SVGSVGElement>>;

const ListaMercatiGlobaliModale: React.FC<Props> = ({
    isOpen,
    onClose,
    title,
    lista,
    onAddToModalList,
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [addedVenues, setAddedVenues] = useState<Venue[]>([]);
    const [searchResults, setSearchResults] = useState<Venue[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const itemsPerPage = 6;

    const filters = useMemo(() => {
        if (title.includes('Italia')) {
            return { iso_country_code_in: 'IT' };
        } else if (title.includes('Estero')) {
            return { iso_country_code_not_in: ['IT'] };
        }
        return undefined;
    }, [title]);

useEffect(() => {
    if (!isOpen) return;

    const searchVenues = async () => {
        if (searchTerm.length >= 3) {
            setIsSearching(true);
            try {
                // Unica chiamata: l'API cerca in OR su tutti i campi
                const results = await listVenues(0, 100, {
                    ...filters,
                    search_text: searchTerm,
                    operating_mics: [searchTerm.toUpperCase()], // Aggiunge anche la ricerca per MIC esatto
                });
                
                setSearchResults(results);
            } catch (error) {
                console.error('Errore nella ricerca delle venues:', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        } else if (searchTerm.length === 0) {
            // Campo vuoto: carica i primi 200 risultati
            setIsSearching(true);
            try {
                const results = await listVenues(0, 200, filters);
                setSearchResults(results);
            } catch (error) {
                console.error('Errore nel caricamento delle venues:', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }
    };

    const timeoutId = setTimeout(searchVenues, 500);
    return () => clearTimeout(timeoutId);
}, [searchTerm, isOpen, filters]);

    if (!isOpen) return null;
    
    const handleAddClick = async (venue: Venue) => {
        const confirmed = await onAddToModalList(venue);
        if (confirmed) {
            setAddedVenues(prev => [...prev, venue]);
            onClose();
        }
    };
    
    const handleRemoveFromAdded = (id?: string | number) => {
        if (id === undefined) return;
        setAddedVenues(prev => prev.filter(v => v.id !== id));
    };

    // Filtra per escludere gli elementi già aggiunti
    const filteredList = searchResults.filter((item) =>
        !addedVenues.some(v => v.id === item.id)
    );

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedList = filteredList.slice(startIndex, endIndex);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-start pt-8">
            <div className="relative p-6 border w-full max-w-7xl shadow-lg rounded-md bg-white">
                {/* Header */}
                <div className="flex justify-between items-center pt-3 pb-6">
                    <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl" aria-label="Chiudi modal">
                        &times;
                    </button>
                </div>
                
                {/* Lista degli elementi aggiunti */}
                {addedVenues.length > 0 && (
                    <div className="mb-4">
                        <p className="text-sm font-semibold text-gray-700 mb-4">Mercati aggiunti:</p>
                        <ul className="list-disc pl-5">
                            {addedVenues.map(venue => (
                                <li key={venue.id} className="text-gray-700">
                                    {venue.market_name}
                                    <button
                                        onClick={() => handleRemoveFromAdded(venue.id)}
                                        className="ml-2 text-red-500 hover:text-red-700"
                                    >
                                        <TrashIcon className="inline h-4 w-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Barra di ricerca con hint */}
                <div className="mb-4">
                    <input
                        type="text"
                        placeholder="Digita almeno 3 caratteri per cercare..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    {searchTerm.length > 0 && searchTerm.length < 3 && (
                        <p className="text-sm text-gray-500 mt-1">
                            Digita ancora {3 - searchTerm.length} carattere{3 - searchTerm.length > 1 ? 'i' : ''} per avviare la ricerca
                        </p>
                    )}
                </div>

                {/* Tabella stile grid */}
                <div className="bg-surface rounded-lg shadow-md overflow-hidden">
                    <div className="grid gap-x-4 p-6 border-b border-divider font-semibold text-textSecondary grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_auto]">
                        <div className="flex items-center">MARKET NAME</div>
                        <div className="flex justify-center items-center">MIC</div>
                        <div className="flex justify-center items-center">OMIC</div>
                        <div className="flex justify-center items-center">LEGAL ENTITY</div>
                        <div className="flex justify-center items-center">ACRONIMO</div>
                        <div className="flex justify-center items-center">STATO</div>
                        <div className="flex justify-center items-center">AZIONI</div>
                    </div>

                    <div className="divide-y divide-divider max-h-[50vh] overflow-y-auto">
                        {isSearching ? (
                            <div className="p-4 text-center text-gray-500">
                                Caricamento in corso...
                            </div>
                        ) : searchTerm.length > 0 && searchTerm.length < 3 ? (
                            <div className="p-4 text-center text-gray-500">
                                Digita almeno 3 caratteri per iniziare la ricerca
                            </div>
                        ) : paginatedList.length > 0 ? (
                            paginatedList.map((item) => (
                                <div
                                    key={item.id || item.mic}
                                    className="grid gap-x-4 p-4 hover:bg-gray-50 transition duration-150 ease-in-out items-center grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_auto]"
                                >
                                    <div className="flex items-center text-textPrimary ml-2">{item.market_name}</div>
                                    <div className="flex justify-center items-center text-primary">{item.mic}</div>
                                    <div className="flex justify-center items-center text-primary">{item.operating_mic}</div>
                                    <div className="flex justify-center items-center text-primary">{item.legal_entity_name}</div>
                                    <div className="flex justify-center items-center text-primary">{item.acronym}</div>
                                    <div className="flex justify-center items-center text-primary">{item.status}</div>

                                    <div className="flex justify-center items-center">
                                        <button
                                            title="Aggiungi alla lista modale"
                                            className="bg-textPrimary-600 hover:text-green-800 p-2 rounded-full hover:bg-gray-200"
                                            onClick={() => handleAddClick(item)}
                                        >
                                            <PlusIcon />
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-4 text-center text-gray-500">
                                {searchTerm.length >= 3 ? 'Nessun mercato trovato con questi criteri' : 'Inizia a digitare per cercare mercati'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ListaMercatiGlobaliModale;